import { useState, useEffect, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import RegretCard from '../components/RegretCard';
import { getRegrets, getStats } from '../api';
import { Regret, CategoryTag, SortOption, CATEGORY_LABELS } from '../types';

const CATEGORIES: (CategoryTag | null)[] = [null, 'love', 'career', 'family', 'money', 'health', 'other'];

export default function Wall() {
  const [regrets, setRegrets] = useState<Regret[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [page, setPage] = useState(1);
  const [category, setCategory] = useState<CategoryTag | null>(null);
  const [sort, setSort] = useState<SortOption>('recent');
  const [totalRegrets, setTotalRegrets] = useState<number>(0);
  const observerRef = useRef<HTMLDivElement>(null);

  // Fetch stats
  useEffect(() => {
    getStats().then(stats => setTotalRegrets(stats.totalRegrets)).catch(() => {});
  }, []);

  // Fetch regrets
  const fetchRegrets = useCallback(async (pageNum: number, reset = false) => {
    if (reset) {
      setLoading(true);
    } else {
      setLoadingMore(true);
    }

    try {
      const response = await getRegrets(pageNum, 50, category, sort);
      if (reset) {
        setRegrets(response.regrets);
      } else {
        setRegrets(prev => [...prev, ...response.regrets]);
      }
      setHasMore(response.pagination.hasMore);
    } catch (error) {
      console.error('Failed to fetch regrets:', error);
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, [category, sort]);

  // Initial load and filter changes
  useEffect(() => {
    setPage(1);
    fetchRegrets(1, true);
  }, [fetchRegrets]);

  // Infinite scroll observer
  useEffect(() => {
    const observer = new IntersectionObserver(
      entries => {
        if (entries[0].isIntersecting && hasMore && !loadingMore && !loading) {
          const nextPage = page + 1;
          setPage(nextPage);
          fetchRegrets(nextPage);
        }
      },
      { threshold: 0.1 }
    );

    if (observerRef.current) {
      observer.observe(observerRef.current);
    }

    return () => observer.disconnect();
  }, [hasMore, loadingMore, loading, page, fetchRegrets]);

  const handleCategoryChange = (newCategory: CategoryTag | null) => {
    setCategory(newCategory);
  };

  const handleSortChange = (newSort: SortOption) => {
    setSort(newSort);
  };

  return (
    <div className="max-w-7xl mx-auto px-4 py-8">
      {/* Live counter */}
      <div className="text-center mb-8">
        <h1 className="text-4xl md:text-5xl font-serif font-semibold text-gray-900 mb-2">
          {totalRegrets.toLocaleString()} regrets.
        </h1>
        <p className="text-xl text-gray-600 font-serif italic">
          {totalRegrets.toLocaleString()} people lighter.
        </p>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center justify-center gap-2 mb-8">
        {/* Category filters */}
        <div className="flex flex-wrap items-center justify-center gap-2">
          {CATEGORIES.map(cat => (
            <button
              key={cat || 'all'}
              onClick={() => handleCategoryChange(cat)}
              className={`
                px-4 py-2 rounded-full text-sm font-medium transition-all
                ${category === cat
                  ? 'bg-gray-900 text-white'
                  : 'bg-white text-gray-600 hover:bg-gray-100 border border-gray-200'
                }
              `}
            >
              {cat ? CATEGORY_LABELS[cat] : 'All'}
            </button>
          ))}
        </div>

        {/* Divider */}
        <div className="hidden sm:block w-px h-6 bg-gray-300 mx-2" />

        {/* Sort options */}
        <div className="flex items-center gap-2">
          <button
            onClick={() => handleSortChange('recent')}
            className={`
              px-4 py-2 rounded-full text-sm font-medium transition-all
              ${sort === 'recent'
                ? 'bg-gray-900 text-white'
                : 'bg-white text-gray-600 hover:bg-gray-100 border border-gray-200'
              }
            `}
          >
            Most Recent
          </button>
          <button
            onClick={() => handleSortChange('likes')}
            className={`
              px-4 py-2 rounded-full text-sm font-medium transition-all
              ${sort === 'likes'
                ? 'bg-gray-900 text-white'
                : 'bg-white text-gray-600 hover:bg-gray-100 border border-gray-200'
              }
            `}
          >
            Most Liked
          </button>
        </div>
      </div>

      {/* Loading state */}
      {loading ? (
        <div className="flex items-center justify-center py-20">
          <div className="animate-pulse-soft text-gray-500">Loading regrets...</div>
        </div>
      ) : regrets.length === 0 ? (
        <div className="text-center py-20">
          <p className="text-gray-500 text-lg mb-4">No regrets yet in this category.</p>
          <Link to="/submit" className="btn btn-primary">
            Be the first — $1
          </Link>
        </div>
      ) : (
        <>
          {/* Masonry grid */}
          <div className="columns-1 sm:columns-2 lg:columns-3 xl:columns-4 gap-4 space-y-4">
            {regrets.map((regret, index) => (
              <div
                key={regret.id}
                className="masonry-item break-inside-avoid"
                style={{ animationDelay: `${(index % 10) * 50}ms` }}
              >
                <RegretCard regret={regret} />
              </div>
            ))}
          </div>

          {/* Load more trigger */}
          <div ref={observerRef} className="h-20 flex items-center justify-center">
            {loadingMore && (
              <div className="animate-pulse-soft text-gray-500">Loading more...</div>
            )}
          </div>
        </>
      )}

      {/* Floating CTA */}
      <div className="fixed bottom-6 right-6 z-40">
        <Link
          to="/submit"
          className="btn btn-primary shadow-lg hover:shadow-xl transform hover:scale-105 transition-all"
        >
          Add yours — $1
        </Link>
      </div>
    </div>
  );
}
